"use client";

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Moon, Sun, Monitor, Check } from "lucide-react";
import { useTheme, type ThemeMode } from "@/lib/theme-context";
import { useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import {
  computePopoverPosition,
  type PopoverPosition,
} from "@/lib/popover-position";

interface ThemeOption {
  value: ThemeMode;
  labelKey: "themeDark" | "themeLight" | "themeSystem";
  icon: typeof Moon;
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: "dark", labelKey: "themeDark", icon: Moon },
  { value: "light", labelKey: "themeLight", icon: Sun },
  { value: "system", labelKey: "themeSystem", icon: Monitor },
];

export function ThemeSelector({ className }: { className?: string }) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [coords, setCoords] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    right: 0,
  });
  const [mounted] = useState(() => typeof document !== "undefined");
  // `resolvedTheme` derives from localStorage, which only exists on the
  // client. Rendering the trigger icon from it during SSR vs hydration would
  // mismatch (server: Moon/dark, client: Sun/light) → React #418 on every
  // page with a header. Gate the icon until the client has mounted.
  const [hydrated, setHydrated] = useState(false);

  const { shouldRender, isLeaving } = useAnimatedOpen(open, 150);

  useEffect(() => {
    const markHydrated = () => setHydrated(true);
    markHydrated();
  }, []);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const updateCoords = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || 160;
    const menuHeight = menuRef.current?.offsetHeight || 180;
    setCoords(computePopoverPosition(r, menuWidth, menuHeight, "end"));
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setFocusIndex(-1);
    buttonRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updateCoords();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
        setFocusIndex(0);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIndex((prev) => (prev + 1) % THEME_OPTIONS.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIndex(
        (prev) => (prev - 1 + THEME_OPTIONS.length) % THEME_OPTIONS.length,
      );
      return;
    }

    if (e.key === "Home") {
      e.preventDefault();
      setFocusIndex(0);
      return;
    }

    if (e.key === "End") {
      e.preventDefault();
      setFocusIndex(THEME_OPTIONS.length - 1);
      return;
    }
  };

  useEffect(() => {
    if (open && focusIndex >= 0 && optionRefs.current[focusIndex]) {
      optionRefs.current[focusIndex]?.focus();
    }
  }, [open, focusIndex]);

  // Determine trigger icon based on selection or active theme
  // (fall back to Monitor until hydration completes to keep SSR/client in sync)
  const TriggerIcon =
    !hydrated || theme === "system"
      ? Monitor
      : resolvedTheme === "light"
        ? Sun
        : Moon;

  return (
    <div className={cn("relative inline-flex items-center", className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("themeSelector")}
        data-testid="theme-selector-button"
        className={cn(
          "group inline-flex size-9 items-center justify-center rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--background-elevated)] text-muted-foreground transition-all duration-200 hover:border-[var(--border-strong)] hover:bg-[var(--muted)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]",
          open &&
            "border-[var(--border-strong)] bg-[var(--muted)] text-foreground",
        )}
      >
        <TriggerIcon
          className="size-4 transition-transform duration-200 group-hover:scale-105"
          aria-hidden="true"
        />
      </button>

      {shouldRender &&
        mounted &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={t("themeSelector")}
            tabIndex={-1}
            data-testid="theme-selector-menu"
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              right: `${coords.right}px`,
              // 2026-09-12 z coherente: 10001 (Language > Theme para convivir en el mismo gear).
              zIndex: 10001,
            }}
            // 2026-09-12: max-w/max-h + z coherente (99999 hundía al resto) → 10001 bajo Language (10000→10001) sobre Dropdown (9999).
            // 2026-09-20 FASE 3: shadow-raised estaba muerto (no existe el utility — el token es
            // var(--shadow-raised)) → shadow-[var(--shadow-panel)]; superficie opaca elevada sin blur
            // (research R2 2026: blur sobre opaco solo cuesta repaint, no aporta).
            className={`w-40 max-w-[calc(100vw-1rem)] max-h-[70dvh] max-h-[min(70dvh,320px)] overflow-y-auto overscroll-contain rounded-[var(--radius-lg)] border border-[var(--border-strong)] bg-[var(--background-elevated)] p-1.5 shadow-[var(--shadow-panel)] ${isLeaving ? "animate-out fade-out-0 zoom-out-95 duration-150" : "animate-in fade-in-0 zoom-in-95 duration-150"}`}
          >
            {THEME_OPTIONS.map((opt, idx) => {
              const Icon = opt.icon;
              const selected = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  ref={(el) => {
                    optionRefs.current[idx] = el;
                  }}
                  role="menuitemradio"
                  aria-checked={selected}
                  data-testid={`theme-option-${opt.value}`}
                  onClick={() => {
                    setTheme(opt.value);
                    close();
                  }}
                  onKeyDown={handleKeyDown}
                  className={cn(
                    "flex w-full items-center justify-between gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-xs font-medium transition-colors outline-none",
                    selected
                      ? "bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] text-foreground"
                      : "text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground focus:bg-[var(--muted)] focus:text-foreground",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon
                      className="size-3.5 shrink-0 opacity-80"
                      aria-hidden="true"
                    />
                    <span>{t(opt.labelKey)}</span>
                  </span>
                  {selected && (
                    <Check
                      className="size-3.5 shrink-0 text-[var(--accent)]"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
